import { Enums,
  getRenderingEngine,
  setVolumesForViewports,
  volumeLoader } from '@cornerstonejs/core';
import { ImageOrientation, Lesions, VolumeIds } from 'types';
import { loadNewVolume, setupTooling } from 'utils/cornerstone/setup';
import { deactivateVesselTools,
  setupVesselReconstructionTools } from 'utils/cornerstone/vesselTools';
import { ChangeOrientation, getViewport } from 'utils/utilities';
import { RENDERING_ENGINE_ID, VIEWPORT_ID } from '../../constants';
import getDeviceType from '../devices';
import { calculateSliceValues } from './tooling';
import cleanUrlParams from '../params';

// Module-level cache
let cameraStateCache: any = null;

export async function resetVesselReconstruction() {
  const currentViewport: any = getViewport();
  if (!currentViewport) {
    return;
  }

  if (cameraStateCache) {
    currentViewport.setCamera(cameraStateCache);
  }
  currentViewport.render();
}

export async function activateVesselReconstruction(vesselData: VolumeIds) {
  const currentViewport = getViewport();
  if (!currentViewport) {
    return;
  }
  const renderingEngine = getRenderingEngine(RENDERING_ENGINE_ID);

  // Initialize specialized tools for vessel manipulation
  const urlParams = new URLSearchParams(window.location.search);
  const { orientation } = currentViewport.defaultOptions;
  const { currentSlice, maxSlice } = calculateSliceValues();
  const toolGroup = setupVesselReconstructionTools();
  const { element } = currentViewport;

  // Vessel volumes are loaded separately from main images
  const volumeId = `nifti:${vesselData.vessel}`;
  await volumeLoader.createAndCacheVolume(volumeId);

  // Preserve current orientation in URL params for restoration later
  urlParams.set('slice', currentSlice.toString());
  urlParams.set('max_slice', maxSlice.toString());
  urlParams.set('orientation', orientation);
  window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);

  // Configure 3D viewport for vessel visualization
  const viewportInput = [
    {
      viewportId: VIEWPORT_ID,
      type: Enums.ViewportType.VOLUME_3D,
      element,
      defaultOptions: { orientation: Enums.OrientationAxis.CORONAL },
    },
  ];

  renderingEngine?.setViewports(viewportInput);
  const viewport = renderingEngine.getViewport(VIEWPORT_ID) as any;

  // Device-specific performance optimization:
  // Higher sample distance = better performance but lower quality
  const deviceType = getDeviceType();
  const sampleDistance = deviceType === 'mobile' ? 5.0 : 2.0; // Mobile needs more conservative settings

  await setVolumesForViewports(
    renderingEngine,
    [
      {
        volumeId,
        callback: ({ volumeActor }) => {
          if (volumeActor) {
            const mapper = volumeActor.getMapper();
            mapper.setSampleDistance(sampleDistance);
            const volumeProperty = volumeActor.getProperty();
            volumeProperty.setShade(false);
          }
        },
      },
    ],
    [VIEWPORT_ID],
  );

  // adding the specific tools related to interacting and navigating the 3d volume
  toolGroup?.addViewport(VIEWPORT_ID, renderingEngine.id);

  // Apply clinical to viewing vessel reconstructions
  viewport.setProperties({
    preset: 'CT-Coronary-Arteries-3', // Standardized Hounsfield unit range
  });

  viewport.render();
  cameraStateCache = viewport.getCamera();
}

export async function deactivateVesselReconstruction(
  activeLesion: Lesions,
  activeVolumesData: VolumeIds,
) {
  const currentViewport = getViewport();
  if (!currentViewport) {
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);
  deactivateVesselTools();
  const renderingEngine = getRenderingEngine(RENDERING_ENGINE_ID);
  if (!renderingEngine) {
    console.error('Rendering engine not found');
    return;
  }

  await loadNewVolume({
    image: activeVolumesData.image,
    lesions: activeVolumesData.lesions,
  });

  // Rest of the orientation and tooling setup remains
  const toolGroup = setupTooling();
  toolGroup.addViewport(VIEWPORT_ID, renderingEngine.id);

  const imageOrientation = urlParams.get('orientation') as ImageOrientation;
  await ChangeOrientation(imageOrientation, activeLesion);

  cleanUrlParams(['orientation', 'slice', 'max_slice']);
}
